import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';

import { useParams, useLocation } from 'react-router-dom';

import { reverse } from 'named-urls';

import { GridValidRowModel } from '@mui/x-data-grid';
import Box from '@mui/material/Box';

import dayjs from 'dayjs';

import Record from 'ui/Record/Record';

import { Button } from 'components/Button';

import { CenteredCircularProgress } from 'components/Progress';

import { MainHeader } from 'components/MainHeader/MainHeader';

import routes from 'core/routes';

import { useStore } from 'utils/hooks/store';

import { useNavBarHeight } from 'utils/hooks/navbar';

import api from 'utils/api';

import Grid from 'components/Grid/Grid';

import { UuidableName } from 'utils/api/types';
import { TeamDetailsResponse } from 'utils/api/modules/teams/teams.types';

import NavigationBlocker from 'components/NavigationBlocker';

import { Container, SectionHeader, TextInput } from './styles';

import AddMembers from './components/AddMembers';
import { basicColumns } from './TeamDetailsColumns';

const TeamsDetails: React.FunctionComponent = () => {
  const navBarHeight = useNavBarHeight();
  const { routing, notification } = useStore();
  const [isNew, setIsNew] = useState(true);
  const params = useParams<{ teamId: string }>();
  const location = useLocation();

  const { teamId } = params;

  useEffect(() => {
    setIsNew(location.pathname === routes.dashboard.settings.teams.new);
    initialValuesRef.current = { ...results };
  }, [location.pathname]);

  React.useEffect(() => {
    (async () => {
      if (teamId) {
        const res = await api.teams.loadTeamDetails(teamId)();
        setResult(res.data);
        initialValuesRef.current = { ...res.data };
      }
    })();
  }, []);

  const onCancel = (): void => {
    routing.push(reverse(routes.dashboard.settings.teams.toString()));
  };

  const [results, setResult] = useState<TeamDetailsResponse>(
    isNew
      ? {
          uuid: '',
          name: `Team created ${dayjs(new Date()).format('DD MMM HH:mm:ss')}`,
          members: [],
        }
      : {}
  );

  const initialValuesRef = useRef<TeamDetailsResponse | null>(null);
  const isDirtyRef = useRef(false);

  const isDirty =
    initialValuesRef.current !== null && JSON.stringify(results) !== JSON.stringify(initialValuesRef.current);
  isDirtyRef.current = isDirty;

  const onSave = async (): Promise<void> => {
    try {
      const teamValues = { name: results?.name || 'default name', members: results?.members || [] };
      if (isNew) {
        await api.teams.createTeam(teamValues)();
        notification.enqueueSuccessSnackbar(`Team created successfully.`);
      } else {
        await api.teams.updateTeam(teamId as string, teamValues)();
        notification.enqueueSuccessSnackbar(`Team updated successfully.`);
      }
      isDirtyRef.current = false;
      initialValuesRef.current = { ...results };
      routing.push(reverse(routes.dashboard.settings.teams.toString()));
    } catch (error) {
      notification.enqueueErrorSnackbar(`Team cannot be ${isNew ? 'created' : 'updated'} right now.`);
    }
  };

  const addMembers = (members: UuidableName[]): void => {
    const newMembers = members.map((member) => ({
      uuid: member.uuid,
      name: member.name,
      isAdmin: false,
    }));

    setResult((prevState) => ({
      ...prevState,
      members: [...(prevState?.members || []), ...newMembers],
    }));
  };

  const transformData = (
    response: unknown
  ): {
    count: number;
    results: GridValidRowModel[];
  } => {
    const members = (response as TeamDetailsResponse).members ?? [];
    return {
      count: members.length,
      results: members.map((m) => ({
        ...m,
        setAdmin: (isAdmin: boolean) =>
          setResult((prevState) => ({
            ...prevState,
            members: (prevState?.members || []).map((i) => (i.uuid === m.uuid ? { ...i, isAdmin } : i)),
          })),
        removeMember: () =>
          setResult((prevState) => ({
            ...prevState,
            members: (prevState?.members || []).filter(({ uuid }) => uuid !== m.uuid),
          })),
      })),
    };
  };

  if (!results) {
    return <CenteredCircularProgress />;
  }

  return (
    <>
      <NavigationBlocker
        when={isDirty}
        shouldBlockNavigation={() => isDirtyRef.current}
        navigateMessage="You have unsaved changes. Do you want to discard them or stay on this page?"
      />
      <Record
        disableMaxContentHeight
        navBarHeight={navBarHeight}
        Content={(onError) => (
          <Container>
            <SectionHeader>Team Name</SectionHeader>
            <TextInput
              fullWidth
              id="team-name"
              value={results.name}
              onChange={(e) => {
                setResult((prevState) => ({ ...prevState, name: e.target.value }));
              }}
            />
            <Grid
              onError={onError}
              dataURL=""
              initialData={transformData(results)}
              basicColumns={basicColumns}
              onRowClick={() => {}}
              disableColumnSelector
              transformData={transformData}
              sortBy={{ field: 'name', sort: 'asc' }}
              showHeader={false}
              showQuickFilter={false}
              customNoRowsText="No users have been added"
              gridPagination={false}
            />
            <AddMembers addMembers={addMembers} membersInTeam={results.members || []} />
          </Container>
        )}
        Header={
          <MainHeader
            title={isNew ? 'Create New Team' : 'Edit Team'}
            breadcrumbs={[
              { label: 'Organisation', link: '' },
              { label: 'Teams', link: routes.dashboard.settings.teams.toString() },
              {
                label: isNew ? 'New Team' : 'Edit Team',
                link: isNew
                  ? routes.dashboard.settings.teams.new.toString()
                  : reverse(routes.dashboard.settings.teams.details, { teamId: teamId as string }),
              },
            ]}
            action={
              <Box sx={{ display: 'flex' }}>
                <Button onClick={onCancel} variant="outlined" size="large">
                  Cancel
                </Button>
                <Button onClick={onSave} variant="contained" size="large" sx={{ marginLeft: '16px' }}>
                  Save
                </Button>
              </Box>
            }
          />
        }
      />
    </>
  );
};

export default observer(TeamsDetails);
